import React, { useEffect } from "react";
import useResourcesCounters from "../../../../Resources/useResourcesCounters";
import WidgetError from "../../WidgetError/WidgetError";
import { Loader, SkeletonLoader, formatNumber } from "@SeemplicityDev/shared";
import "./ResourcesCountKPI.scss";
import useSeverityAsPriority, {
  SeverityFieldsEnum,
} from "../../../../General/useSeverityAsPriority";
import useRetry from "../../../useRetry";
import { filterEntityEnum } from "../../../../AppView/FilterManagement/filterEntity";
import type { SelectedScope } from "../../../../types/scope";

export interface FindingResourcesCountKPIProps {
  selectedScope?: SelectedScope | null;
  filterToExecute?: string | null;
  scopeToExecute?: string | null;
  filterFieldsToOverride?: string | null;
  severity?: number | string | null;
  setIsReadyToExport?: ((isReady: boolean) => void) | null;
}

const FindingResourcesCountKPI: React.FC<FindingResourcesCountKPIProps> = ({
  selectedScope,
  filterToExecute,
  scopeToExecute,
  filterFieldsToOverride = null,
  severity = null,
  setIsReadyToExport = null,
}) => {
  const { getSeverityField } = useSeverityAsPriority();

  const {
    resourcesCountData: {
      resourcesCounterError,
      resourcesCounterLoading,
      count,
      refetchResourcesCounterData,
    },
    // useResourcesCounters is plain JS; its param types are inferred from the
    // RESOURCES/null defaults, so filterType narrows to "RESOURCES" and
    // filterFieldsToOverride to null. At runtime it accepts any
    // filterEntityEnum value and forwards filterFieldsToOverride as-is (a JSON
    // string here), so widen the argument to the real contract.
  } = useResourcesCounters({
    selectedScope,
    filterToExecute,
    scopeToExecute,
    filterType: filterEntityEnum.REMEDIATION_ITEM,
    filterFieldsToOverride,
  } as unknown as Parameters<typeof useResourcesCounters>[0]);

  const { finalError, resetRetries } = useRetry({
    requestError: resourcesCounterError,
    refetch: refetchResourcesCounterData,
  });

  useEffect(() => {
    if (count !== null && !resourcesCounterLoading) {
      setIsReadyToExport?.(true);
      resetRetries();
    }
  }, [count, resourcesCounterLoading]);

  useEffect(() => {
    if (!!finalError) {
      setIsReadyToExport?.(true);
    }
  }, [finalError]);

  useEffect(() => {
    setIsReadyToExport?.(!resourcesCounterLoading);
  }, [resourcesCounterLoading]);

  return (
    <div className={"resources-count-kpi-container"}>
      {!finalError ? (
        !resourcesCounterLoading && count === 0 ? (
          <Loader
            data-testid={"kpi-no-data"}
            className={"empty-state-no-match-to-filter"}
            center
            noBackground
            icon={"seem-icon-manualfinding"}
            subtitle={"There are no Resources for this Scope and Filter"}
          />
        ) : (
          <>
            {!resourcesCounterLoading ? (
              <span className={"kpi-title"}>{formatNumber(count)}</span>
            ) : (
              <SkeletonLoader width={"160px"} height={"42px"} />
            )}
            <div className={"resources-count-legend"}>
              <div
                className={"legend-icon"}
                style={{
                  backgroundColor: getSeverityField(
                    severity ?? undefined,
                    SeverityFieldsEnum.COLOR
                  ),
                }}
              />
              <span className={"legend-title"}>Resources</span>
            </div>
          </>
        )
      ) : (
        <WidgetError onWidgetsReload={refetchResourcesCounterData} />
      )}
    </div>
  );
};

export default FindingResourcesCountKPI;
